import { assertGraphicsRequirementSet } from '@/graphics/GraphicsRequirements'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'
import type { ActivatableToolPlugin, AssetPlugin, Plugin } from '@/shared/types/plugin'
import type { EngineFrameContext, ViewFrameContext } from '@/shared/types/view'
import type { EngineContext } from './EngineContext'

const log = getLoggerManager().getLogger('runtime:plugins')

// ─── 只读查询接口（通过 PotreeEngine 暴露给宿主） ───

/** 只读 Plugin 查询接口 */
export interface PluginQuery {
  /**
   * 获取全部已注册插件名称。
   *
   * @returns 插件名数组，顺序与注册顺序一致
   */
  getNames(): string[]
  /**
   * 判断插件是否已注册。
   *
   * @param name - 目标插件名
   * @returns 已注册返回 true，否则 false
   */
  has(name: string): boolean
  /**
   * 按名称获取具体 Plugin 实例（escape hatch）。
   *
   * 调用方承担类型耦合风险。未找到返回 undefined。
   *
   * @param name - 目标插件名
   * @returns 插件实例，未注册返回 undefined
   */
  get<T extends Plugin>(name: string): T | undefined
}

// ─── PluginManager 实现 ───

interface PluginEntry {
  plugin: Plugin
  registrationOrder: number
  lifecycle: PluginLifecycleLedger
}

interface PluginLifecycleLedger {
  preloadStarted: boolean
  preloadFulfilled: boolean
  preloadRolledBack: boolean
  initialized: boolean
  started: boolean
  destroyed: boolean
}

/**
 * Plugin 管理器 —— 拓扑排序 + 生命周期调度
 *
 * 框架内部对象，不对宿主暴露 add/init/destroy。
 * 宿主通过 PotreeEngine.plugins（PluginQuery）只读查询。
 */
export class PluginManager implements Disposable, PluginQuery {
  private plugins: PluginEntry[] = []
  private initialized = false
  private initializing = false
  private destroyed = false
  private referencesReleased = false
  private context: EngineContext | null = null
  private nextOrder = 0

  // ── 排序缓存（init 后生成） ──
  private topoSorted: Plugin[] = []
  private updateSorted: Plugin[] = []
  private destroySorted: Plugin[] = []

  // ─── 注册 ───

  /**
   * 追加 Plugin。
   *
   * name 重复抛错，init() 后禁止再调用。
   *
   * @param plugin - 待注册插件实例
   */
  add(plugin: Plugin): void {
    if (this.destroyed) {
      throw new Error('PluginManager 已销毁，禁止再添加 Plugin')
    }
    if (this.initialized) {
      throw new Error('PluginManager 已初始化，禁止再添加 Plugin')
    }
    if (this.plugins.some((e) => e.plugin.name === plugin.name)) {
      throw new Error(`Plugin "${plugin.name}" 已注册，name 不可重复`)
    }
    this.plugins.push({
      plugin,
      registrationOrder: this.nextOrder++,
      lifecycle: {
        preloadStarted: false,
        preloadFulfilled: false,
        preloadRolledBack: false,
        initialized: false,
        started: false,
        destroyed: false,
      },
    })
  }

  /**
   * 批量追加插件。
   *
   * @param plugins - 待注册插件数组，按顺序逐个追加
   */
  addAll(plugins: Plugin[]): void {
    for (const p of plugins) {
      this.add(p)
    }
  }

  // ─── PluginQuery 实现 ───

  /**
   * 按名称获取插件实例。
   *
   * @param name - 目标插件名
   * @returns 插件实例，未注册返回 undefined
   */
  get<T extends Plugin>(name: string): T | undefined {
    const entry = this.plugins.find((e) => e.plugin.name === name)
    return entry ? (entry.plugin as T) : undefined
  }

  /**
   * 获取全部已注册插件名称。
   *
   * @returns 插件名数组，顺序与注册顺序一致
   */
  getNames(): string[] {
    return this.plugins.map((e) => e.plugin.name)
  }

  /**
   * 判断插件是否已注册。
   *
   * @param name - 目标插件名
   * @returns 已注册返回 true，否则 false
   */
  has(name: string): boolean {
    return this.plugins.some((e) => e.plugin.name === name)
  }

  /**
   * 返回所有 AssetPlugin 实例。
   *
   * @returns 同时实现 load 与 mount 的插件数组
   */
  getAssetPlugins(): AssetPlugin[] {
    return this.plugins
      .map((e) => e.plugin)
      .filter((p): p is AssetPlugin => 'load' in p && 'mount' in p)
  }

  /**
   * 返回所有 ActivatableToolPlugin 实例。
   *
   * @returns 同时实现 activate 与 deactivate 的插件数组
   */
  getToolPlugins(): ActivatableToolPlugin[] {
    return this.plugins
      .map((e) => e.plugin)
      .filter((p): p is ActivatableToolPlugin => 'activate' in p && 'deactivate' in p)
  }

  /**
   * 返回所有 Plugin 实例。
   *
   * @returns 已注册插件数组，顺序与注册顺序一致
   */
  getAll(): Plugin[] {
    return this.plugins.map((e) => e.plugin)
  }

  // ─── 初始化（异步） ───

  /**
   * 初始化所有已注册插件。
   *
   * 完整流程：
   * 1. 重复 name 检测
   * 2a. dependency 可解析性校验
   * 2b. provider 唯一性校验
   * 3+4. 拓扑排序 + 循环依赖检测（Kahn 算法）
   * 5. await Promise.allSettled(preload())，失败时逆序 rollbackPreload
   * 6. onInit（按拓扑顺序）
   * 7. onStart（按拓扑顺序）
   *
   * @param context - 引擎上下文，插件 onInit 时接收
   */
  async init(context: EngineContext): Promise<void> {
    if (this.destroyed) {
      throw new Error('PluginManager 已销毁，禁止初始化')
    }
    if (this.initialized) {
      throw new Error('PluginManager 已初始化，禁止重复调用')
    }
    if (this.initializing) {
      throw new Error('PluginManager 正在初始化中，禁止并发调用')
    }

    this.initializing = true
    const initStart = getLoggerManager().now()
    try {
      this.context = context
      log.debug(`[PluginManager] init started, plugins=${this.plugins.length}`)

      // Step 1: 重复 name 检测
      const names = this.plugins.map((e) => e.plugin.name)
      const seen = new Set<string>()
      for (const n of names) {
        if (seen.has(n)) {
          throw new Error(`Plugin name 重复: "${n}"`)
        }
        seen.add(n)
      }

      // Step 2a: dependency 可解析性
      const allProvides = new Set<string>()
      for (const { plugin } of this.plugins) {
        if (plugin.provides) {
          for (const svc of plugin.provides) {
            allProvides.add(svc)
          }
        }
      }
      for (const { plugin } of this.plugins) {
        if (plugin.dependencies) {
          for (const dep of plugin.dependencies) {
            if (!allProvides.has(dep)) {
              throw new Error(
                `Plugin "${plugin.name}" 依赖服务 "${dep}"，但没有任何 Plugin 在 provides 中声明该服务`
              )
            }
          }
        }
      }

      // Step 2b: provider 唯一性
      const providerMap = new Map<string, string[]>()
      for (const { plugin } of this.plugins) {
        if (plugin.provides) {
          for (const svc of plugin.provides) {
            const providers = providerMap.get(svc) ?? []
            providers.push(plugin.name)
            providerMap.set(svc, providers)
          }
        }
      }
      for (const [svc, providers] of Array.from(providerMap.entries())) {
        if (providers.length > 1) {
          throw new Error(
            `服务 "${svc}" 被多个 Plugin 同时 provides: [${providers.join(', ')}]，禁止重复提供`
          )
        }
      }

      // Step 3+4: 拓扑排序 + 循环依赖检测
      this.topoSorted = this.topologicalSort()
      if (log.isEnabled(LogLevel.DEBUG)) {
        log.debug(
          `[PluginManager] topo sorted, order=${this.topoSorted.map((plugin) => plugin.name).join(',')}`
        )
      }

      // 生成 update / destroy 排序
      this.buildUpdateAndDestroyOrder()

      // Step 5: graphics requirements gate（任何插件生命周期副作用之前）
      assertGraphicsRequirementSet(
        this.plugins.map(({ plugin }) => ({
          subject: plugin,
          identity: { type: 'plugin', name: plugin.name },
        })),
        context.graphics
      )

      // Step 6: preload
      await this.runPreloads()

      // Step 7: onInit（按拓扑顺序）
      for (const plugin of this.topoSorted) {
        const start = getLoggerManager().now()
        plugin.onInit?.(context)
        this.requireEntry(plugin).lifecycle.initialized = true
        log.debug(
          `[PluginManager] onInit completed, plugin=${plugin.name}, elapsed=${(getLoggerManager().now() - start).toFixed(1)}ms`
        )
      }

      // Step 8: onStart（按拓扑顺序）
      for (const plugin of this.topoSorted) {
        const start = getLoggerManager().now()
        plugin.onStart?.()
        this.requireEntry(plugin).lifecycle.started = true
        log.debug(
          `[PluginManager] onStart completed, plugin=${plugin.name}, elapsed=${(getLoggerManager().now() - start).toFixed(1)}ms`
        )
      }

      this.initialized = true
      log.debug(
        `[PluginManager] init completed, plugins=${this.plugins.length}, elapsed=${(getLoggerManager().now() - initStart).toFixed(1)}ms`
      )
    } catch (err) {
      await this.rollbackFulfilledPreloads(err)
      this.destroyInitializedPlugins()
      log.error(
        `[PluginManager] init failed, elapsed=${(getLoggerManager().now() - initStart).toFixed(1)}ms, reason=${
          err instanceof Error ? err.message : String(err)
        }`
      )
      throw err
    } finally {
      this.initializing = false
    }
  }

  /** Kahn 拓扑排序算法 */
  private topologicalSort(): Plugin[] {
    const entries = this.plugins

    const serviceToPlugin = new Map<string, string>()
    for (const { plugin } of entries) {
      if (plugin.provides) {
        for (const svc of plugin.provides) {
          serviceToPlugin.set(svc, plugin.name)
        }
      }
    }

    const graph = new Map<string, Set<string>>()
    const inDegree = new Map<string, number>()
    for (const { plugin } of entries) {
      graph.set(plugin.name, new Set())
      inDegree.set(plugin.name, 0)
    }

    for (const { plugin } of entries) {
      if (plugin.dependencies) {
        for (const dep of plugin.dependencies) {
          const providerName = serviceToPlugin.get(dep)
          if (providerName && providerName !== plugin.name) {
            const edges = graph.get(providerName)!
            if (!edges.has(plugin.name)) {
              edges.add(plugin.name)
              inDegree.set(plugin.name, (inDegree.get(plugin.name) ?? 0) + 1)
            }
          }
        }
      }
    }

    const queue: string[] = []
    for (const { plugin } of entries) {
      if ((inDegree.get(plugin.name) ?? 0) === 0) {
        queue.push(plugin.name)
      }
    }

    const sorted: string[] = []
    while (queue.length > 0) {
      const current = queue.shift()!
      sorted.push(current)
      const neighbors = graph.get(current)
      for (const neighbor of neighbors ? Array.from(neighbors) : []) {
        const newDegree = (inDegree.get(neighbor) ?? 1) - 1
        inDegree.set(neighbor, newDegree)
        if (newDegree === 0) {
          queue.push(neighbor)
        }
      }
    }

    if (sorted.length !== entries.length) {
      const inCycle = entries
        .filter(({ plugin }) => !sorted.includes(plugin.name))
        .map(({ plugin }) => plugin.name)
      throw new Error(`检测到循环依赖，涉及 Plugin: [${inCycle.join(' → ')}]`)
    }

    const nameToPlugin = new Map(entries.map((e) => [e.plugin.name, e.plugin]))
    return sorted.map((name) => nameToPlugin.get(name)!)
  }

  /**
   * 构建 update 和 destroy 排序。
   * 排序稳定性硬约束：同 priority 保持注册顺序。
   */
  private buildUpdateAndDestroyOrder(): void {
    const sorted = [...this.plugins].sort((a, b) => {
      const pa = a.plugin.priority ?? 0
      const pb = b.plugin.priority ?? 0
      if (pa !== pb) return pa - pb
      return a.registrationOrder - b.registrationOrder
    })

    this.updateSorted = sorted.map((e) => e.plugin)
    this.destroySorted = [...this.updateSorted].reverse()
  }

  /** 执行 preload，失败时逆序 rollbackPreload */
  private async runPreloads(): Promise<void> {
    const preloadStart = getLoggerManager().now()
    const preloadCount = this.plugins.filter(({ plugin }) => plugin.preload).length
    log.debug(`[PluginManager] preload started, plugins=${preloadCount}`)
    const tasks = this.plugins.map(async (entry) => {
      const { plugin, lifecycle } = entry
      if (!plugin.preload) {
        return { entry, status: 'skipped' as const }
      }
      lifecycle.preloadStarted = true
      const start = getLoggerManager().now()
      await plugin.preload(this.context!)
      lifecycle.preloadFulfilled = true
      log.debug(
        `[PluginManager] preload completed, plugin=${plugin.name}, elapsed=${(getLoggerManager().now() - start).toFixed(1)}ms`
      )
      return { entry, status: 'fulfilled' as const }
    })

    const settled = await Promise.allSettled(tasks)
    const results = settled.map((item, index) => {
      if (item.status === 'fulfilled') {
        return item.value
      }
      return {
        entry: this.plugins[index],
        status: 'rejected' as const,
        error: item.reason,
      }
    })

    const failed = results.filter((r) => r.status === 'rejected')
    if (failed.length === 0) {
      log.debug(
        `[PluginManager] preload settled, fulfilled=${preloadCount}, elapsed=${(getLoggerManager().now() - preloadStart).toFixed(1)}ms`
      )
      return
    }

    const reason = (failed[0] as { error: unknown }).error
    log.error(
      `[PluginManager] preload failed, failed=${failed.length}, reason=${
        reason instanceof Error ? reason.message : String(reason)
      }`
    )
    await this.rollbackFulfilledPreloads(reason)
    throw reason
  }

  /** 只回滚已 fulfilled、尚未完成 onInit 且未回滚的 preload。 */
  private async rollbackFulfilledPreloads(reason: unknown): Promise<void> {
    for (const entry of [...this.plugins].reverse()) {
      const { plugin, lifecycle } = entry
      if (!lifecycle.preloadFulfilled || lifecycle.preloadRolledBack || lifecycle.initialized) {
        continue
      }
      lifecycle.preloadRolledBack = true
      try {
        await plugin.rollbackPreload?.(reason)
        log.debug(`[PluginManager] rollbackPreload completed, plugin=${plugin.name}`)
      } catch (rollbackErr) {
        log.warn(
          `[PluginManager] rollbackPreload failed, plugin=${plugin.name}, reason=${
            rollbackErr instanceof Error ? rollbackErr.message : String(rollbackErr)
          }`
        )
      }
    }
  }

  private requireEntry(plugin: Plugin): PluginEntry {
    const entry = this.plugins.find((candidate) => candidate.plugin === plugin)
    if (!entry) throw new Error(`Plugin lifecycle entry 不存在: "${plugin.name}"`)
    return entry
  }

  /** 按既有 destroy order 只销毁完成 onInit 且尚未销毁的插件。 */
  private destroyInitializedPlugins(): void {
    for (const plugin of this.destroySorted) {
      const entry = this.requireEntry(plugin)
      if (!entry.lifecycle.initialized || entry.lifecycle.destroyed) continue
      entry.lifecycle.destroyed = true
      try {
        const start = getLoggerManager().now()
        plugin.onDestroy?.()
        log.debug(
          `[PluginManager] onDestroy completed, plugin=${plugin.name}, elapsed=${(getLoggerManager().now() - start).toFixed(1)}ms`
        )
      } catch (err) {
        log.error(
          `[PluginManager] onDestroy failed, plugin=${plugin.name}, reason=${
            err instanceof Error ? err.message : String(err)
          }`
        )
        this.context?.events.emit('engine.error', {
          error: err instanceof Error ? err : new Error(String(err)),
          context: `destroy:${plugin.name}`,
        })
      }
    }
  }

  // ─── 每帧更新 ───

  /**
   * 按 priority 升序遍历调用 onUpdateFrame(engineFrame)。
   *
   * @param frame - 引擎级帧上下文
   */
  updateFrame(frame: EngineFrameContext): void {
    for (const plugin of this.updateSorted) {
      try {
        plugin.onUpdateFrame?.(frame)
      } catch (err) {
        this.context?.events.emit('engine.error', {
          error: err instanceof Error ? err : new Error(String(err)),
          context: `updateFrame:${plugin.name}`,
        })
      }
    }
  }

  /**
   * 按 priority 升序遍历 view 渲染钩子。
   *
   * @param frame - 视口级帧上下文
   */
  prepareViews(frames: readonly ViewFrameContext[], drawViewIds: readonly string[]): void {
    for (const stage of ['onPrepareViews', 'onViewsPrepared'] as const) {
      for (const plugin of this.updateSorted) {
        try {
          plugin[stage]?.(frames, drawViewIds)
        } catch (err) {
          this.context?.events.emit('engine.error', {
            error: err instanceof Error ? err : new Error(String(err)),
            context: `${stage}:${plugin.name}`,
          })
        }
      }
    }
  }

  renderView(frame: ViewFrameContext): void {
    for (const plugin of this.updateSorted) {
      if (!plugin.onRenderView) {
        continue
      }
      try {
        plugin.onRenderView(frame)
      } catch (err) {
        this.context?.events.emit('engine.error', {
          error: err instanceof Error ? err : new Error(String(err)),
          context: `renderView:${plugin.name}`,
        })
      }
    }
  }

  // ─── 销毁 ───

  /**
   * 按 priority 降序遍历调用 onDestroy()。
   * 错误隔离：try-catch 包裹每个 onDestroy()，emit engine.error 并继续销毁。
   */
  destroy(): void {
    if (this.destroyed) {
      return
    }
    this.destroyed = true
    const destroyStart = getLoggerManager().now()
    log.debug(`[PluginManager] destroy started, plugins=${this.destroySorted.length}`)
    this.destroyInitializedPlugins()
    log.debug(
      `[PluginManager] destroy completed, plugins=${this.destroySorted.length}, elapsed=${(getLoggerManager().now() - destroyStart).toFixed(1)}ms`
    )
  }

  /** 在 engine.disposed 事件广播完成后清空 Plugin 与 EngineContext 强引用。 */
  releaseReferences(): void {
    if (this.referencesReleased) {
      return
    }
    if (!this.destroyed) {
      this.destroy()
    }
    this.referencesReleased = true
    this.context = null
    this.plugins.length = 0
    this.topoSorted.length = 0
    this.updateSorted.length = 0
    this.destroySorted.length = 0
  }

  /** 等同于 destroy()，满足 Disposable 约定 */
  dispose(): void {
    this.destroy()
    this.releaseReferences()
  }
}
